"use client";

import { useEffect, useRef, useState } from "react";
import {
  Check,
  Coffee,
  Dumbbell,
  Pause,
  Play,
  Repeat2,
  RotateCcw,
  SlidersHorizontal,
  Timer,
  Volume2,
  VolumeX,
} from "lucide-react";
import { getTimerState, type TimerSettings } from "@/lib/interval-timer";

const DEFAULT_SETTINGS: TimerSettings = { exercise: 30, rest: 20, rounds: 8 };
const SETTINGS_STORAGE_KEY = "everyday.exercise-timer.settings";
const PREPARATION_SECONDS = 3;

const SETTING_FIELDS = [
  {
    key: "exercise" as const,
    label: "Exercise time",
    unit: "seconds",
    icon: Dumbbell,
    min: 10,
    max: 120,
    step: 10,
  },
  {
    key: "rest" as const,
    label: "Rest time",
    unit: "seconds",
    icon: Coffee,
    min: 0,
    max: 120,
    step: 10,
  },
  {
    key: "rounds" as const,
    label: "Repeats",
    unit: "rounds",
    icon: Repeat2,
    min: 1,
    max: 15,
    step: 1,
  },
];

function normalizeSetting(
  value: number,
  min: number,
  max: number,
  step: number,
) {
  const roundedValue = min + Math.round((value - min) / step) * step;
  return Math.max(min, Math.min(max, roundedValue));
}

function readSavedSettings(): TimerSettings {
  try {
    const saved: unknown = JSON.parse(
      localStorage.getItem(SETTINGS_STORAGE_KEY) ?? "null",
    );
    if (!saved || typeof saved !== "object") return DEFAULT_SETTINGS;

    const restored = { ...DEFAULT_SETTINGS };
    for (const { key, min, max, step } of SETTING_FIELDS) {
      const value = (saved as Record<string, unknown>)[key];
      if (typeof value === "number" && Number.isFinite(value)) {
        restored[key] = normalizeSetting(value, min, max, step);
      }
    }
    return restored;
  } catch {
    // Missing, corrupt, or unavailable storage should not prevent a workout.
    return DEFAULT_SETTINGS;
  }
}

function formatTime(seconds: number) {
  const minutes = Math.floor(seconds / 60)
    .toString()
    .padStart(2, "0");
  const remainingSeconds = (seconds % 60).toString().padStart(2, "0");
  return `${minutes}:${remainingSeconds}`;
}

export function ExerciseTimer() {
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [elapsed, setElapsed] = useState(0);
  const [running, setRunning] = useState(false);
  const [sound, setSound] = useState(true);
  const [audioError, setAudioError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const startedAt = useRef(0);
  const audio = useRef<AudioContext | null>(null);
  const lastPlayedCue = useRef("");
  // Negative elapsed time represents preparation, before the workout begins.
  const preparing = elapsed < 0;
  const countdown = Math.ceil(-elapsed);
  const state = getTimerState(Math.max(0, elapsed), settings);
  const complete = state.phase === "complete";
  const settingsLocked = running || elapsed > 0;

  useEffect(() => {
    // Read browser storage after hydration so server and initial client HTML match.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSettings(readSavedSettings());
  }, []);

  function beep(frequency: number, count = 1) {
    const context = audio.current;
    if (!context || context.state !== "running") return;
    for (let i = 0; i < count; i++) {
      const oscillator = context.createOscillator();
      const gain = context.createGain();
      const start = context.currentTime + i * 0.22;
      oscillator.frequency.value = frequency;
      gain.gain.setValueAtTime(0, start);
      gain.gain.linearRampToValueAtTime(0.16, start + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.001, start + 0.16);
      oscillator.connect(gain);
      gain.connect(context.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.18);
      oscillator.onended = () => {
        oscillator.disconnect();
        gain.disconnect();
      };
    }
  }

  async function enableAudio() {
    try {
      audio.current ??= new AudioContext();
      await audio.current.resume();
      setAudioError(audio.current.state !== "running");
    } catch {
      setAudioError(true);
    }
  }

  // Measure elapsed time from a timestamp to avoid interval drift.
  useEffect(() => {
    if (!running) return;
    const tick = () => {
      const nextElapsed = Math.min(
        (performance.now() - startedAt.current) / 1000,
        state.total,
      );
      if (nextElapsed < 0) {
        const cue = `prepare-${Math.ceil(-nextElapsed)}`;
        if (cue !== lastPlayedCue.current) {
          lastPlayedCue.current = cue;
          if (sound) beep(560);
        }
        setElapsed(nextElapsed);
        return;
      }

      const next = getTimerState(nextElapsed, settings);
      const cue = `${next.round}-${next.phase}-${next.remaining <= 3 ? next.remaining : "start"}`;
      if (cue !== lastPlayedCue.current) {
        const previous = lastPlayedCue.current;
        lastPlayedCue.current = cue;
        if (sound) {
          if (next.phase === "complete") beep(880, 3);
          else if (!previous.startsWith(`${next.round}-${next.phase}-`))
            beep(next.phase === "exercise" ? 740 : 440, 2);
          else if (next.remaining <= 3) beep(560);
        }
      }
      setElapsed(nextElapsed);
      if (next.phase === "complete") setRunning(false);
    };
    const id = window.setInterval(tick, 100);
    return () => window.clearInterval(id);
  }, [running, settings, sound, state.total]);

  useEffect(
    () => () => {
      void audio.current?.close();
    },
    [],
  );

  function start() {
    if (sound) void enableAudio();
    const newSession = complete || elapsed === 0;
    const startElapsed = newSession ? -PREPARATION_SECONDS : elapsed;
    if (newSession) {
      setElapsed(startElapsed);
      lastPlayedCue.current = "";
    }
    startedAt.current = performance.now() - startElapsed * 1000;
    setRunning(true);
  }

  function updateSetting(
    key: keyof TimerSettings,
    input: string,
    min: number,
    max: number,
    step: number,
  ) {
    const value = Number(input);
    const nextSettings = {
      ...settings,
      [key]: normalizeSetting(
        Number.isFinite(value) ? value : min,
        min,
        max,
        step,
      ),
    };
    setSettings(nextSettings);

    // Save only user changes, never the defaults shown before storage is read.
    try {
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(nextSettings));
      setSaveError(false);
    } catch {
      setSaveError(true);
    }
  }

  function toggleSound() {
    if (!sound) void enableAudio();
    setSound(!sound);
  }

  function toggleTimer() {
    if (preparing) {
      reset();
      return;
    }

    if (!running) {
      start();
      return;
    }

    const elapsedSeconds = (performance.now() - startedAt.current) / 1000;
    setElapsed(Math.min(elapsedSeconds, state.total));
    setRunning(false);
  }

  function reset() {
    setRunning(false);
    setElapsed(0);
    lastPlayedCue.current = "";
  }
  const label = preparing
    ? "Get ready"
    : complete
      ? "Well done!"
      : elapsed === 0 && !running
        ? "Ready when you are"
        : state.phase === "rest"
          ? "Breathe & recover"
          : "Time to move";
  const progress = complete
    ? 100
    : ((state.duration - state.remaining) / state.duration) * 100;

  return (
    <main className="page-shell timer-page">
      <div className="eyebrow">
        <Timer size={15} /> MAKE TIME TO MOVE
      </div>
      <h1>
        Find your rhythm<span>.</span>
      </h1>
      <p className="page-description">
        Your pace. Your intervals. One round at a time.
      </p>
      <div className="timer-layout">
        <section className="settings-panel" aria-labelledby="settings-heading">
          <div className="panel-title">
            <SlidersHorizontal size={18} />
            <h2 id="settings-heading">Make it your own</h2>
          </div>
          <p className="panel-description">Set up your next little victory.</p>
          <fieldset disabled={settingsLocked} className="settings-fields">
            {SETTING_FIELDS.map(
              ({ key, label, unit, icon: Icon, min, max, step }) => (
                <label className="setting" key={key} htmlFor={key}>
                  <span className="setting-label">
                    <Icon size={17} />
                    {label}
                    <output className="setting-value" htmlFor={key}>
                      {settings[key]} {unit}
                    </output>
                  </span>
                  <input
                    className="setting-slider"
                    id={key}
                    type="range"
                    min={min}
                    max={max}
                    step={step}
                    value={settings[key]}
                    aria-valuetext={`${settings[key]} ${unit}`}
                    onChange={(event) =>
                      updateSetting(key, event.target.value, min, max, step)
                    }
                  />
                  <span className="slider-limits" aria-hidden="true">
                    <span>
                      {min} {unit}
                    </span>
                    <span>
                      {max} {unit}
                    </span>
                  </span>
                </label>
              ),
            )}
          </fieldset>
          <p className="settings-hint" role="status">
            {saveError
              ? "Your browser could not save these settings. They will last for this visit."
              : "Settings are saved automatically in this browser."}
          </p>
          <div className="session-total">
            <span>Total session</span>
            <strong>{formatTime(state.total)}</strong>
          </div>
          <p className="settings-hint">
            {settingsLocked
              ? "Reset the timer to change your intervals."
              : "Rest between rounds. Finish on a high."}
          </p>
          <div className="sound-row">
            <span>
              <Volume2 size={18} />
              <span>
                Sound cues<small>A little nudge to keep you going</small>
              </span>
            </span>
            <button
              type="button"
              className={`sound-switch ${sound ? "on" : ""}`}
              role="switch"
              aria-checked={sound}
              aria-label="Sound cues"
              onClick={toggleSound}
            >
              <span />
            </button>
          </div>
          {audioError && (
            <p role="status" className="settings-hint">
              Sound is unavailable in this browser. The visual timer still
              works.
            </p>
          )}
        </section>
        <section
          className={`timer-panel ${state.phase === "rest" ? "resting" : ""}`}
          aria-label="Workout timer"
        >
          <div className="timer-topline">
            <span className="phase-badge">
              {preparing ? (
                <Timer size={14} />
              ) : complete ? (
                <Check size={14} />
              ) : state.phase === "rest" ? (
                <Coffee size={14} />
              ) : (
                <Dumbbell size={14} />
              )}
              {preparing
                ? "GET READY"
                : complete
                  ? "SESSION COMPLETE"
                  : state.phase === "rest"
                    ? "REST"
                    : "EXERCISE"}
            </span>
            {sound ? <Volume2 size={17} /> : <VolumeX size={17} />}
          </div>
          <div className="timer-dial">
            <svg viewBox="0 0 300 300" aria-hidden="true">
              <circle className="dial-track" cx="150" cy="150" r="137" />
              <circle
                className="dial-progress"
                cx="150"
                cy="150"
                r="137"
                pathLength="100"
                strokeDasharray="100"
                strokeDashoffset={progress}
              />
            </svg>
            <div className="dial-content">
              <p className="dial-label">{label}</p>
              <span
                className="timer-digits"
                role="timer"
                aria-label={
                  preparing
                    ? `Starting in ${countdown} seconds`
                    : `${state.remaining} seconds remaining`
                }
              >
                {preparing ? countdown : formatTime(state.remaining)}
              </span>
              <span className="round-label">
                ROUND {state.round} <span>/ {settings.rounds}</span>
              </span>
            </div>
          </div>
          <p className="timer-status" aria-live="polite">
            {preparing
              ? `Starting in ${countdown}. Get into position.`
              : complete
                ? "You showed up. That’s what counts."
                : running
                  ? state.phase === "rest"
                    ? "Relax your shoulders. You’ve earned this break."
                    : "Keep going. You’ve got this."
                  : elapsed > 0
                    ? "Take your time. Resume when you’re ready."
                    : "Take a breath. Then take the first step."}
          </p>
          <div className="timer-controls">
            <button
              type="button"
              className="start-button"
              onClick={toggleTimer}
            >
              {preparing ? (
                <RotateCcw size={19} />
              ) : running ? (
                <Pause size={19} />
              ) : (
                <Play size={19} />
              )}
              {preparing
                ? "Cancel"
                : running
                  ? "Pause"
                  : complete
                    ? "Go again"
                    : elapsed > 0
                      ? "Resume"
                      : "Start session"}
            </button>
            <button
              type="button"
              className="reset-button"
              onClick={reset}
              aria-label="Reset timer"
              title="Reset timer"
            >
              <RotateCcw size={20} />
            </button>
          </div>
          <div
            className="session-progress"
            aria-label={`Round ${state.round} of ${settings.rounds}`}
          >
            <div
              style={{
                width: `${complete ? 100 : ((state.round - 1) / settings.rounds) * 100}%`,
              }}
            />
          </div>
          <p className="timer-footnote">
            {settings.rounds} rounds · {settings.exercise}s exercise ·{" "}
            {settings.rest}s rest
          </p>
        </section>
      </div>
      <p className="home-note">
        A little movement goes a long way. Keep this page open during your
        session.
      </p>
    </main>
  );
}
